import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Motion from '@/components/base/Motion'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'

import { imageParagraph } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

export type TextOnImageProps = HTMLAttributes<HTMLElement> &
  Partial<{
    image: WPImage
    paragraph: {
      title?: string
      paragraph?: string
      button: WPLink
    }
    alignment: 'left' | 'center' | 'right'
    text_alignment: 'left' | 'center' | 'right'
  }>

const TextOnImage = ({
  image,
  paragraph,
  alignment = 'right',
  text_alignment = 'center',
  className,
  ...props
}: TextOnImageProps) => {
  return (
    <Motion animation={imageParagraph}>
      {(motionRef) => (
        <section
          ref={motionRef}
          className={cn(['relative w-full overflow-hidden', className])}
          {...props}
        >
          <div className="relative aspect-[9/16] w-full md:aspect-[1440/1000]">
            {image?.url ? (
              <>
                <Image
                  source={image}
                  size="full"
                  fill
                  className="absolute inset-0 h-full w-full object-cover"
                  data-motion-image
                />
                <div className="absolute inset-0 z-10 bg-black/50" />
              </>
            ) : null}
            <div className="absolute inset-0 z-10">
              <Container className="flex h-full items-center">
                <Grid className="h-full">
                  <div
                    className={cn([
                      'col-span-4 flex flex-col justify-center gap-[2.5rem] py-[4rem]',
                      {
                        'items-start md:col-start-2': alignment === 'left',
                        'items-center md:col-start-4': alignment === 'center',
                        'items-start md:col-start-8': alignment === 'right',
                        'text-left': text_alignment === 'left',
                        'items-center text-center': text_alignment === 'center',
                        'items-end text-right': text_alignment === 'right'
                      }
                    ])}
                  >
                    {paragraph?.title ? (
                      <Typography
                        variant="h1"
                        className="text-white"
                        component="h2"
                        data-motion-title
                      >
                        {paragraph.title}
                      </Typography>
                    ) : null}
                    {paragraph?.paragraph ? (
                      <Typography
                        variant="body"
                        className="text-white"
                        data-motion-paragraph
                      >
                        {paragraph.paragraph}
                      </Typography>
                    ) : null}
                    {paragraph?.button?.url &&
                    paragraph?.button?.url !== '#' ? (
                      <div data-motion-cta>
                        <Button
                          asChild
                          type="button"
                          variant="dark"
                          icon={
                            <SVGIcon
                              symbol="chevron-right-icon"
                              className="h-[11px] w-[7px] shrink-0 text-current"
                            />
                          }
                        >
                          <Link
                            href={paragraph.button.url}
                            target={paragraph.button.target}
                            data-label={paragraph.button.title}
                            scroll={false}
                          >
                            <Typography variant="button">
                              {paragraph.button.title}
                            </Typography>
                          </Link>
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </Grid>
              </Container>
            </div>
          </div>
        </section>
      )}
    </Motion>
  )
}

export default TextOnImage
